import { type ReactElement, useState } from 'react'
import { FileUp, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type ProductBomImportPreview,
  type ProductBomImportResult,
  useImportProductBom,
  usePreviewProductBomImport,
} from '@/shared/hooks/api/catalog'
import { isAllowedBomImportFile } from '@/shared/lib/bomImportFileTypes'
import {
  isBomImportUnsupportedFileTypeApiError,
  resolveBomImportUploadErrorMessage,
} from '@/shared/lib/resolveBomImportUploadError'
import { BomImportUploadDialog } from '@/shared/components/bom/BomImportUploadDialog'
import { BomImportReviewDialog } from '@/shared/components/bom/BomImportReviewDialog'
import { buildV2BomImportReviewLabels } from '@/shared/lib/bomImportReviewLabels'
import {
  evaluateBomImportRows,
  type EvaluatedBomImportRow,
} from '@/shared/lib/bomImportRowValidation'
import { Button } from '@/components/ui/button'

type V2ImportBomButtonProps = {
  productId: string
  onImported?: (result: ProductBomImportResult) => void
}

function reviewSkippedFailures(
  rows: EvaluatedBomImportRow[]
): ProductBomImportResult['failed'] {
  return rows
    .filter((row) => !row.valid)
    .map((row) => ({
      row: row.rowNumber,
      sku: row.sku === '' ? undefined : row.sku,
      reason: row.reason ?? 'sku is required',
    }))
}

export function V2ImportBomButton({
  productId,
  onImported,
}: V2ImportBomButtonProps): ReactElement {
  const { t } = useTranslation('common')
  const { mutateAsync: previewBom, isPending: isPreviewPending } =
    usePreviewProductBomImport(productId)
  const { mutateAsync: importBom, isPending: isImportPending } =
    useImportProductBom(productId)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  const [isSelectingFile, setIsSelectingFile] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [reviewPreview, setReviewPreview] =
    useState<ProductBomImportPreview | null>(null)

  function showImportSuccess(result: ProductBomImportResult) {
    toast.success(
      t('v2.pages.products.detail.parts.import.success', {
        createdSuppliers: result.createdSuppliers,
        createdComponents: result.createdComponents,
        updatedComponents: result.updatedComponents,
        linked: result.linked,
      })
    )
    onImported?.(result)
  }

  async function runPreview(file: File) {
    setIsSelectingFile(true)
    try {
      const preview = await previewBom({ file })
      setUploadDialogOpen(false)
      setFileError(null)
      setPendingFile(file)
      setReviewPreview(preview)
    } catch (error) {
      const failureMessage = resolveBomImportUploadErrorMessage(
        error,
        t('v2.pages.products.detail.parts.import.errors.unsupportedFileType'),
        t('v2.pages.products.detail.parts.import.errors.importFailed')
      )
      if (isBomImportUnsupportedFileTypeApiError(error)) {
        setFileError(failureMessage)
        return
      }
      toast.error(failureMessage)
    } finally {
      setIsSelectingFile(false)
    }
  }

  function handleFileSelected(file: File) {
    if (!isAllowedBomImportFile(file)) {
      setFileError(
        t('v2.pages.products.detail.parts.import.errors.unsupportedFileType')
      )
      return
    }

    setFileError(null)
    void runPreview(file)
  }

  async function handleConfirmImport(input: {
    mapping: {
      skuColumn: number
      supplierColumn: number
      nameColumn?: number | null
    }
    importRowNumbers: number[]
  }) {
    if (pendingFile == null || reviewPreview == null) {
      return
    }

    const evaluated = evaluateBomImportRows(reviewPreview.rows, input.mapping)
    const invalidCount = evaluated.filter((row) => !row.valid).length

    try {
      const result = await importBom({
        file: pendingFile,
        mapping: input.mapping,
        importRowNumbers: input.importRowNumbers,
      })

      const skippedInReview = reviewSkippedFailures(evaluated)
      const mergedResult: ProductBomImportResult = {
        ...result,
        failed: [...skippedInReview, ...result.failed],
      }

      setReviewPreview(null)
      setPendingFile(null)

      if (invalidCount > 0) {
        toast.warning(
          t('v2.pages.products.detail.parts.import.partialWarning', {
            count: invalidCount,
          })
        )
        onImported?.(mergedResult)
        return
      }

      if (result.failed.length === 0) {
        showImportSuccess(result)
        return
      }

      onImported?.(mergedResult)
    } catch {
      toast.error(t('v2.pages.products.detail.parts.import.errors.importFailed'))
    }
  }

  const isBusy = isPreviewPending || isImportPending || isSelectingFile

  return (
    <>
      <Button
        className="gap-2"
        disabled={isBusy}
        onClick={() => {
          setFileError(null)
          setUploadDialogOpen(true)
        }}
        size="sm"
        type="button"
        variant="outline"
      >
        {isBusy ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <FileUp className="size-4" />
        )}
        {t('v2.pages.products.detail.parts.import.button')}
      </Button>
      <BomImportUploadDialog
        fileError={fileError}
        isBusy={isBusy}
        labels={{
          title: t('v2.pages.products.detail.parts.import.uploadDialog.title'),
          formatsRequirement: t(
            'v2.pages.products.detail.parts.import.supportedFileTypes'
          ),
          dragDrop: t(
            'v2.pages.products.detail.parts.import.uploadDialog.dragDrop'
          ),
          orClick: t(
            'v2.pages.products.detail.parts.import.uploadDialog.orClick'
          ),
          selectFile: t(
            'v2.pages.products.detail.parts.import.uploadDialog.selectFile'
          ),
        }}
        open={uploadDialogOpen}
        onFileSelected={handleFileSelected}
        onOpenChange={(open) => {
          if (isBusy) {
            return
          }
          setUploadDialogOpen(open)
          if (!open) {
            setFileError(null)
          }
        }}
      />
      <BomImportReviewDialog
        preview={reviewPreview}
        isPending={isImportPending}
        labels={buildV2BomImportReviewLabels(t)}
        onOpenChange={(open) => {
          if (!open) {
            setReviewPreview(null)
            setPendingFile(null)
          }
        }}
        onConfirm={(input) => {
          void handleConfirmImport(input)
        }}
      />
    </>
  )
}
